"use client";
import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from '@/navigation';
import CategoryGrid from './CategoryGrid';
import ProductList from './ProductList';
import { fetchCategoriesData, fetchProductByCategoryData } from '@/lib/api/product';

interface Category {
  id: string;
  slug: string;
  name: string;
  translation?: {
    name: string;
    description?: string;
  };
  media?: string | null;
  children?: {
    edges: Array<{
      node: Category;
    }>;
  };
  products?: {
    totalCount: number;
  };
}

interface ProductsPageProps {
  initialCategories: Category[];
  channel: string;
  locale: string;
  slug?: string;
  initialProducts?: any;
}

export default function ProductsPage({
  initialCategories,
  channel,
  locale,
  slug,
  initialProducts
}: ProductsPageProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [currentCategories, setCurrentCategories] = useState<Category[]>(initialCategories);
  const [currentProducts, setCurrentProducts] = useState(initialProducts);
  const [categoryHistory, setCategoryHistory] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'categories' | 'products'>('categories');

  // 如果有 slug，说明是分类页面，优先显示商品
  useEffect(() => {
    if (slug) {
      // 优先检查是否有商品
      if (initialProducts && initialProducts.edges && initialProducts.edges.length > 0) {
        // 有商品，直接显示商品列表
        setViewMode('products');
        setCurrentProducts(initialProducts);
      } else if (initialCategories && initialCategories.length > 0) {
        // 没有商品但有子分类，尝试加载商品
        loadCategoryProducts(slug);
      } else {
        // 既没有商品也没有子分类，尝试加载
        loadCategoryChildren(slug);
      }
    } else {
      // 没有 slug，显示根分类
      setViewMode('categories');
      setCurrentCategories(initialCategories);
    }
  }, [slug, initialCategories, initialProducts]);

  // 加载分类的商品
  const loadCategoryProducts = async (categorySlug: string) => {
    try {
      setLoading(true);
      const productData = await fetchProductByCategoryData({ slug: categorySlug, locale, channel });
      if (productData?.category?.products?.edges?.length > 0) {
        setCurrentProducts(productData.category.products);
        setViewMode('products');
      } else {
        // 没有商品，显示子分类
        setCurrentCategories(initialCategories);
        setViewMode('categories');
      }
    } catch (error) {
      console.error('Error loading category products:', error);
      // 出错时显示子分类
      setCurrentCategories(initialCategories);
      setViewMode('categories');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryChildren = async (categorySlug: string) => {
    try {
      setLoading(true);
      const result = await fetchCategoriesData({ locale, channel });
      const allCategories = result?.categories?.edges?.map((edge: any) => edge.node) || [];
      
      // 查找当前分类
      const findCategory = (categories: Category[], slug: string): Category | null => {
        for (const category of categories) {
          if (category.slug === slug) {
            return category;
          }
          if (category.children?.edges) {
            const found = findCategory(
              category.children.edges.map((e: any) => e.node),
              slug
            );
            if (found) return found;
          }
        }
        return null;
      };

      const currentCategory = findCategory(allCategories, categorySlug);
      
      // 优先尝试加载商品
      const productData = await fetchProductByCategoryData({ slug: categorySlug, locale, channel });
      if (productData?.category?.products?.edges?.length > 0) {
        setCurrentProducts(productData.category.products);
        setViewMode('products');
      } else if (currentCategory?.children?.edges && currentCategory.children.edges.length > 0) {
        // 没有商品，但有子分类，显示子分类
        setCurrentCategories(currentCategory.children.edges.map((e: any) => e.node));
        setViewMode('categories');
        setCategoryHistory(prev => [...prev, currentCategory]);
      } else {
        // 既没有商品也没有子分类，显示空状态
        setViewMode('products');
        setCurrentProducts({ edges: [], pageInfo: { hasNextPage: false } });
      }
    } catch (error) {
      console.error('Error loading category children:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (category: Category) => {
    // 导航到分类页面
    router.push(`/products/${category.slug}`);
  };

  const handleBackClick = () => {
    if (categoryHistory.length > 0) {
      const previousCategory = categoryHistory[categoryHistory.length - 1];
      const newHistory = categoryHistory.slice(0, -1);
      setCategoryHistory(newHistory);
      
      // 如果历史记录为空，返回根分类
      if (newHistory.length === 0) {
        router.push('/products');
        setCurrentCategories(initialCategories);
        setViewMode('categories');
      } else {
        // 返回上一级分类
        const parentCategory = newHistory[newHistory.length - 1];
        setCurrentCategories(parentCategory.children?.edges?.map((e: any) => e.node) || []);
        router.push(`/products/${parentCategory.slug}`);
      }
    } else {
      // 返回根分类
      router.push('/products');
      setCurrentCategories(initialCategories);
      setViewMode('categories');
    }
    setCurrentProducts(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mainColor mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'products' && currentProducts) {
    return (
      <>
        {categoryHistory.length > 0 && (
          <div className="container mx-auto px-4 pt-8">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-gray-600 hover:text-mainColor transition-colors mb-4"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span>Back to Categories</span>
            </button>
          </div>
        )}
        <ProductList 
          products={currentProducts} 
          locale={locale} 
          channel={channel} 
          slug={slug}
        />
      </>
    );
  }

  return (
    <CategoryGrid
      categories={currentCategories}
      onCategoryClick={handleCategoryClick}
      showBackButton={categoryHistory.length > 0}
      onBackClick={handleBackClick}
      parentCategoryName={categoryHistory.length > 0 ? categoryHistory[categoryHistory.length - 1]?.translation?.name || categoryHistory[categoryHistory.length - 1]?.name : undefined}
      isFirstLevel={!slug}
    />
  );
}

